import type { UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import type {
	CiselnikyCiselnikPolozkaSeznamDto,
	OsobyEditorSeznamDto,
	OsobyPodnikatelRozsireneDto,
	ResponseError,
} from '@gov-nx/api';
import {
	editorsOvmQuery,
	legalFormQuery,
	sendComplaintQueryRos,
	usePoMutation,
	usePoQueries,
	useAxiosInstance,
} from '@gov-nx/api';
import { GovError } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	useAddressSearch,
	useProcessControl,
	useServiceAuthorization,
} from '@gov-nx/core/hooks';
import type { Nullable } from '@gov-nx/core/types';
import type { PageCode } from '../../definitions/codes';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type {
	FormData,
	ProfileROSComplaintContext,
	ProfileRosComplaintProcessControl,
} from './context.types';
import type { RosComplaintVariant } from './options';
import { useRosComplaintOptions } from './options';
import {
	getEmailContact,
	getPhoneContact,
	getSortedLegalFormOptions,
	interruptionToBoolean,
} from './utils';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

const ProfilROSReklamaceContext =
	createContext<Nullable<ProfileROSComplaintContext>>(null);

interface ProfilROSComplaintContextProviderProps {
	children: React.ReactNode;
	code: PageCode;
	data: OsobyPodnikatelRozsireneDto;
	variant: RosComplaintVariant;
	onComplaintSubmit?: () => void;
}

const AUTHORIZATION_SERVICE_NAME = 'PODANI_REKLAMACE_ROS';

export function ProfilROSReklamaceContextProvider({
	children,
	code,
	data,
	variant,
	onComplaintSubmit,
}: ProfilROSComplaintContextProviderProps) {
	const { setControls, controls } =
		useProcessControl<ProfileRosComplaintProcessControl>({
			initialLoading: true,
			displaySearchAddressLoading: false,
			displaySearchAddress: false,
			initialLegalFormError: null,
			initialEditorsError: null,
		});
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { t } = useTranslation([code]);
	const { axiosInstance } = useAxiosInstance();
	const authContext = useAutorizaceContext();
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const [rosLegalFormResult, rosEditorsResult] = usePoQueries<
		[
			UseQueryResult<
				CiselnikyCiselnikPolozkaSeznamDto,
				AxiosError<ResponseError>
			>,
			UseQueryResult<OsobyEditorSeznamDto, AxiosError<ResponseError>>,
		]
	>({
		queries: [
			{
				queryKey: ['profile-ros-legal-form'],
				queryFn: async () => legalFormQuery(axiosInstance),
				onError: async (error: Error) => {
					setControls({ initialLegalFormError: new GovError(error.message) });
				},
			},
			{
				queryKey: ['profile-ros-editors', data.ico],
				queryFn: () => editorsOvmQuery(axiosInstance, data.ico),
				onError: async (error: Error) => {
					setControls({ initialEditorsError: new GovError(error.message) });
				},
			},
		],
		onAllSuccess: () => {
			setControls({ initialLoading: false });
		},
	});

	const legalForm = rosLegalFormResult.data?.seznam ?? [];
	const editors = rosEditorsResult.data?.seznam ?? [];

	const formDefinition = FormInstance({
		code,
		data,
		controls,
		isLowAuthorization,
	});

	const { address, mainQuery } = useAddressSearch({
		formMethods: formDefinition.formMethods,
		onSuccess: () => setControls({ displaySearchAddressLoading: false }),
		onError: () => setControls({ displaySearchAddressLoading: false }),
	});

	const newAddress = formDefinition.formMethods.watch('newAddress');

	useEffect(() => {
		const selectedOption = address.find((option) => option.kod === newAddress);
		if (selectedOption && address.length > 0) {
			formDefinition.formMethods.setValue(
				'adresaSidla',
				selectedOption.adresaText || ''
			);
		}
	}, [newAddress, address, formDefinition.formMethods]);

	const submitMutation = usePoMutation({
		mutationFn: async (formData: FormData) => {
			const prepared = prepareSubmitData(
				newAddress,
				editors,
				data.ico
			)(formData);
			return sendComplaintQueryRos(axiosInstance, prepared);
		},
		onError: (error) => {
			toastMessageError(t('formular.zprava.neodeslano'));
			setControls({ processError: error, processLoading: false });
		},
		onSuccess: async (data) => {
			setControls({ processLoading: false });
			formDefinition.formMethods.reset();
			onComplaintSubmit?.();

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(t('formular.zprava.odeslano'));
			};

			if (authContext) {
				authContext?.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const resetAddressSearchFields = () => {
		formDefinition.formMethods.resetField('nazevObce');
		formDefinition.formMethods.resetField('nazevUlice');
		formDefinition.formMethods.resetField('cislo');
	};

	const handleSubmit = useCallback(
		async (newData: FormData) => {
			setControls({ processError: null, processLoading: true });
			const {
				nazevSpolecnosti,
				agenda,
				datumOd,
				datumDo,
				pravniForma,
				pozastaveniPreruseni,
				adresaSidla,
				popisReklamace,
			} = newData;
			const changedValues: FormData = {};

			if (nazevSpolecnosti !== data.nazevSpolecnosti) {
				changedValues.nazevSpolecnosti = nazevSpolecnosti;
			}

			if (agenda?.selected?.kod !== data.agenda?.kod) {
				changedValues.agenda = agenda;
			}

			if (datumOd !== data.datumVzniku) {
				changedValues.datumOd = datumOd;
			}

			if (datumDo !== data.datumZaniku) {
				changedValues.datumDo = datumDo;
			}

			if (pravniForma !== data.pravniForma?.kod) {
				changedValues.pravniForma = pravniForma;
			}
			const initialInterruption = data.pozastaveniPreruseni ?? false;
			if (interruptionToBoolean(pozastaveniPreruseni) !== initialInterruption) {
				changedValues.pozastaveniPreruseni = pozastaveniPreruseni;
			}

			if (adresaSidla !== data.sidloSpolecnosti?.adresaText) {
				changedValues.adresaSidla = adresaSidla;
			}

			changedValues._telefon = getPhoneContact(newData);
			changedValues._email = getEmailContact(newData);

			changedValues.popisReklamace = popisReklamace;

			if (Object.keys(changedValues).length > 0) {
				submitMutation.mutate({
					...changedValues,
				});
			} else {
				setControls({ processLoading: false });
			}
		},
		[
			setControls,
			data.nazevSpolecnosti,
			data.agenda?.kod,
			data.datumVzniku,
			data.datumZaniku,
			data.pravniForma?.kod,
			data.pozastaveniPreruseni,
			data.sidloSpolecnosti?.adresaText,
			submitMutation,
		]
	);

	const onSubmit = formDefinition.formMethods.handleSubmit(handleSubmit);

	const sortedOptions = getSortedLegalFormOptions(legalForm);
	const selectedLegalForm = formDefinition.formMethods.getValues('pravniForma');

	const filteredOptions = sortedOptions.filter(
		(option) => option.label !== selectedLegalForm
	);

	const { interruptionOptions } = useRosComplaintOptions();

	return (
		<ProfilROSReklamaceContext.Provider
			value={{
				controls,
				setControls,
				variant,
				formDefinition,
				data,
				legalForm,
				onSubmit,
				address,
				selectedLegalForm,
				filteredOptions,
				interruptionOptions,
				mainQuery,
				resetAddressSearchFields,
				disableSubmitButton:
					!formDefinition.formMethods.watch('kontaktTelefon') &&
					!formDefinition.formMethods.watch('kontaktEmail'),
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ProfilROSReklamaceContext.Provider>
	);
}

export const useProfilROSReklamaceContextInstance =
	(): ProfileROSComplaintContext =>
		useContext(ProfilROSReklamaceContext) as ProfileROSComplaintContext;
